import { ApiService } from 'app/api.service';
import { AppService } from 'app/app.service';
import { Pagination } from 'app/shared/pagination';

export class ProductSearch {

	// Matches MIN_SEARCH_LENGTH in api-globalsearch.php - below this there's nothing worth scanning for.
	minSearchLength = 3;
	maxRecentSearches = 5;

	// `null` means a search is in flight; `[]` means it found nothing.
	results: any[] = [];
	query = '';
	facets: any = null;
	filterSections: any[] = [];
	filterChips: any[] = [];
	selectedFilters: any = {};
	scope: any = { segment: null, category: null, subcategories: [] };
	segments: any[] = [];
	browseCategories: any[] = [];
	totalCategories = 0;
	browseLoading = false;
	totalGroups = 0;
	capped = false;

	// The count is a floor rather than a total: the scan stopped before it ran out of matches.
	approximate = false;
	suggestions: any[] = [];
	recentSearches: string[] = [];
	showRecentSearches = false;
	pagination = new Pagination();
	constraints: any = {};

	// "sectionId:value" -> { label, color } for every facet value the panel has offered, so a chip keeps its name
	// after the value drops out of the facets. See composeChips.
	private filterLabels: any = {};

	// Which organisation the loaded category tree belongs to.
	private browseKey = null;

	/**
	 * The term the results on screen were found with, as opposed to `query`, which is whatever is in the box right
	 * now. Null until a search has actually been asked for, which is what keeps the browse view up - and the last
	 * results on screen - while someone is still typing.
	 */
	private submittedTerm = null;

	// The request the results on screen came from, so an earlier response landing late can't overwrite them.
	private requestId = 0;

	// The last result set and the request that produced it, so re-running an unchanged search costs nothing.
	private lastSignature = null;
	private lastResults = null;

	get term() {
		return this.query.trim();
	}

	get hasScope() {
		return !!(this.scope.segment || this.scope.category || this.scope.subcategories?.length);
	}

	get activeFilterIds() {
		return ['inStock', 'brands', 'distributors'].filter(id => (this.selectedFilters[id] || []).length > 0);
	}

	get hasFilters() {
		return this.hasScope || this.activeFilterIds.length > 0;
	}

	// A search needs either a long enough term or somewhere drilled to; anything less isn't worth a round trip.
	// This is what enables the Search button, so it reads the box rather than what was last searched for.
	get canSearch() {
		return this.term.length >= this.minSearchLength || this.hasScope;
	}

	// Until a search has been asked for, the caller shows the browse view instead of a result table.
	get browsing() {
		return this.submittedTerm === null && !this.hasScope;
	}

	get loading() {
		return this.results === null;
	}

	// Only offered when the box is focused and empty - once you're typing, the category suggestions take over.
	get showsRecentSearches() {
		return this.showRecentSearches && !this.term && this.recentSearches.length > 0;
	}

	/**
	 * Currency for the price column. The products search doesn't convert, so this is the organisation's own.
	 */
	get currencyId() {
		return this.app.orgInfo?.primary_currency_id || null;
	}

	constructor(private api: ApiService, private app: AppService) {
		this.loadRecentSearches();
	}

	/**
	 * Typed into the search box. Nothing is fetched: the keyword match can't use an index, so a scan is far too
	 * expensive to spend on a term nobody has finished typing. Only the local category suggestions keep up.
	 */
	onQueryInput(): void {
		this.updateSuggestions();
	}

	// The Search button and the Enter key. Everything on screen stays as it was until this is asked for.
	search(): void {
		this.showRecentSearches = false;
		this.suggestions = [];
		if (this.term) this.recordRecentSearch(this.term);
		this.run();
	}

	// Runs the search as it currently stands, or restores the last results if nothing about it has changed.
	run(): void {
		if (!this.canSearch) {
			this.reset();
			return;
		}

		// From here the results belong to what is in the box, not to whatever was searched for before.
		this.submittedTerm = this.term;

		const signature = this.signature();
		if (signature === this.lastSignature && this.lastResults) {
			this.apply(this.lastResults);
			return;
		}

		this.results = null;
		const id = ++this.requestId;

		this.api.globalSearch.searchProducts(
			this.request(),
			(data) => {
				// A later search may have landed while this was in flight; its results win.
				if (id !== this.requestId) return;
				this.lastSignature = signature;
				this.lastResults = data;
				this.apply(data);
			},
			(error) => {
				if (id !== this.requestId) return;
				this.results = [];
				this.app.notifications.showDanger(error.message);
			}
		);
	}

	// Filtering happens on the backend: a union group survives if any of its copies matches.
	applyFilters(filters): void {
		this.selectedFilters = filters || {};
		this.run();
	}

	// The sidebar drilled somewhere new - re-run with the new scope, keeping any keyword.
	scopeChanged(scope): void {
		this.scope = scope || { segment: null, category: null, subcategories: [] };
		this.run();
	}

	removeScope(level): void {
		if (level === 'segment') this.scope = { segment: null, category: null, subcategories: [] };
		if (level === 'category') this.scope = { ...this.scope, category: null, subcategories: [] };
		if (level === 'subcategories') this.scope = { ...this.scope, subcategories: [] };
		this.run();
	}

	// Drops one facet value, leaving the rest of the selection alone.
	removeFilter(chip): void {
		const next = { ...this.selectedFilters };
		const values = (next[chip.section_id] || []).filter(value => value !== chip.value);

		if (values.length) {
			next[chip.section_id] = values;
		} else {
			delete next[chip.section_id];
		}

		this.applyFilters(next);
	}

	clearFilters(): void {
		this.scope = { segment: null, category: null, subcategories: [] };
		this.selectedFilters = {};
		this.run();
	}

	clearSearch(): void {
		this.query = '';
		this.suggestions = [];
		this.run();
	}

	recentSearch(query): void {
		this.query = query;
		this.showRecentSearches = false;
		this.suggestions = [];
		this.run();
	}

	// Browsing into a category scopes the search to it rather than typing its name as a keyword.
	browseCategory(category): void {
		this.scope = { segment: category.segment, category: category.category, subcategories: [] };
		this.run();
	}

	applySuggestion(category): void {
		this.suggestions = [];
		this.query = '';
		this.browseCategory(category);
	}

	/**
	 * The browse view - category groups down the side and the browsable category list - covers the organisation's
	 * whole catalogue, so it's loaded once per organisation. Called when the search is first shown rather than on
	 * construction: the picker is built before an organisation is in context.
	 */
	loadBrowse(): void {
		const key = this.app.org;

		// Already loaded for this organisation, or no organisation to load for.
		if (!key || key === this.browseKey) return;

		this.browseKey = key;
		this.browseLoading = true;

		this.api.globalSearch.browse(
			'products',
			(data) => {
				if (this.browseKey !== key) return;
				this.segments = data.segments || [];
				this.browseCategories = data.categories || [];
				this.totalCategories = data.total_categories || 0;
				this.browseLoading = false;
			},
			() => {
				if (this.browseKey !== key) return;
				this.segments = [];
				this.browseCategories = [];
				this.totalCategories = 0;
				this.browseKey = null;
				this.browseLoading = false;
				// A failed browse shouldn't stop someone searching.
			}
		);
	}

	// Suggestions come from the classification tree we already hold, so there's no round trip per keystroke.
	updateSuggestions(): void {
		const term = this.query.trim().toLowerCase();
		if (term.length < 2) {
			this.suggestions = [];
			return;
		}

		this.suggestions = this.browseCategories
			.filter(category => (category.category || '').toLowerCase().includes(term))
			.slice(0, 5);
	}

	private request() {
		const options: any = {
			query: this.term,
			in_stock: (this.selectedFilters['inStock'] || []).length > 0,
			brands: this.selectedFilters['brands'] || [],
			catalogue_ids: this.selectedFilters['distributors'] || [],
			segment: this.scope.segment,
			category: this.scope.category,
			subcategories: this.scope.subcategories
		};

		// Only what the host actually restricts. A null constraint isn't sent, so the backend leaves it open.
		if (this.constraints?.sold_to_customer != null) options.sold_to_customer = this.constraints.sold_to_customer;
		if (this.constraints?.is_bundle != null) options.is_bundle = this.constraints.is_bundle;

		return options;
	}

	// Everything the results depend on: the term, the filters and the drill-down.
	private signature(): string {
		return JSON.stringify({
			term: this.term,
			filters: this.selectedFilters || {},
			scope: this.scope,
			constraints: this.constraints || {}
		});
	}

	private apply(data): void {
		this.results = data.list || [];
		this.facets = data.facets || null;
		this.capped = !!data.capped;
		this.approximate = !!data.approximate;

		// Under the cap the count is real. Over it, the scan stopped at a number of rows and those rows folded into
		// fewer groups, so the count is what the sample found and the "+" beside it says there is more. The row cap
		// itself is not the number to show - 2,000 rows fold into far fewer groups, so quoting it over-reports.
		const total = data.total_groups !== undefined ? data.total_groups : (data.total || 0);
		this.totalGroups = total;

		// The page index belongs to the list being replaced.
		this.pagination.page = 1;

		this.buildFilterSections();
	}

	private reset(): void {
		this.submittedTerm = null;
		this.results = [];
		this.facets = null;
		this.totalGroups = 0;
		this.capped = false;
		this.approximate = false;
		this.pagination.page = 1;
		this.buildFilterSections();
	}

	/**
	 * The filter sections, built from the facet counts the backend returned. Facets describe a result set, so
	 * there's nothing to show until a search has run.
	 *
	 * Held as fields rather than getters: a getter hands Angular a new array every change-detection cycle, which
	 * re-creates the checkbox elements underneath the pointer and swallows the click.
	 */
	private buildFilterSections(): void {
		this.filterSections = this.composeSections();
		// Recorded before the chips are built, so a value still in the facets refreshes its own remembered name.
		this.rememberFilterLabels();
		this.filterChips = this.composeChips();
	}

	private composeSections() {
		if (this.browsing || !this.facets) return [];

		return [
			this.flagSection('inStock', 'Availability', 'In stock', this.facets.in_stock),
			{ id: 'brands', name: 'Brands', hasSearch: true, values: this.toValues(this.facets.brands) },
			{ id: 'distributors', name: 'Distributor', hasSearch: true, values: this.toDistributorValues(this.facets.distributors) }
		];
	}

	/**
	 * The selected facet values, as chips beside the scope ones so it's visible what is narrowing the results
	 * without opening the panel.
	 *
	 * Labels come from the facet sections, then the section's own label for a single-checkbox section, then the
	 * last one seen for that value. Those fallbacks matter: facets describe a result set, so a search that matches
	 * nothing returns none at all - and the raw value is a catalogue id for a distributor, or just `yes` for a flag.
	 */
	private composeChips() {
		const chips = [];

		this.activeFilterIds.forEach(sectionId => {
			const section = this.filterSections.find(s => s.id === sectionId);

			(this.selectedFilters[sectionId] || []).forEach(value => {
				const entry = (section?.values || []).find(v => v.value === value);
				const remembered = this.filterLabels[sectionId + ':' + value];

				chips.push({
					section_id: sectionId,
					value,
					label: entry?.label || section?.flagLabel || remembered?.label || value,
					color: entry?.color || remembered?.color || null
				});
			});
		});

		return chips;
	}

	/**
	 * Keeps the name and colour of every facet value the panel has offered, so a chip can still say what it is
	 * once the value has dropped out of the facets.
	 */
	private rememberFilterLabels(): void {
		this.filterSections.forEach(section => {
			(section.values || []).forEach(value => {
				this.filterLabels[section.id + ':' + value.value] = { label: value.label, color: value.color || null };
			});
		});
	}

	private toValues(facet) {
		return (facet || []).map(entry => ({
			value: entry.value,
			label: entry.label || entry.value,
			count: entry.cnt,
			color: entry.color
		}));
	}

	private toDistributorValues(facet) {
		return (facet || []).map(entry => ({ value: entry.catalogue_id, label: entry.name, count: entry.cnt }));
	}

	/**
	 * A single-checkbox section, e.g. "In stock". Its one value is just the flag `yes`, so the label is kept on
	 * the section as well: facets describe a result set, so a search matching nothing returns a count of zero and
	 * no checkbox at all, and without this the chip would have nothing to read but `yes`.
	 */
	private flagSection(id, name, label, count) {
		return {
			id,
			name,
			expandable: false,
			flagLabel: label,
			values: count ? [{ value: 'yes', label, count }] : []
		};
	}

	private recordRecentSearch(query: string): void {
		const index = this.recentSearches.indexOf(query);
		if (index !== -1) this.recentSearches.splice(index, 1);

		this.recentSearches.unshift(query);

		if (this.recentSearches.length > this.maxRecentSearches) {
			this.recentSearches = this.recentSearches.slice(0, this.maxRecentSearches);
		}

		localStorage.setItem('recentSearches', JSON.stringify(this.recentSearches));
	}

	/**
	 * Shared with the advanced search on purpose - the same person searching for the same things.
	 *
	 * Guarded because this runs from the constructor, which the product picker calls as it opens: unparseable or
	 * unexpected localStorage would otherwise stop the picker opening at all, everywhere it is used, over a list
	 * of past search terms that nothing depends on.
	 */
	private loadRecentSearches(): void {
		const stored = localStorage.getItem('recentSearches');
		if (!stored) return;

		try {
			const parsed = JSON.parse(stored);
			if (Array.isArray(parsed)) this.recentSearches = parsed.slice(0, this.maxRecentSearches);
		} catch (e) {
			this.recentSearches = [];
		}
	}
}
